import { createApp } from 'vue'
import { createPinia } from 'pinia'
import CarbonVue from '@carbon/vue'
import App from './App.vue'
import router, { markAuthInitialized } from './router'
import i18n, { loadRemoteMessages } from './plugins/i18n'
import { useAuthStore } from './stores/auth.store'
import { loadConfig } from './config'
import { initClient } from './api/client'

// Carbon Design System styles
import 'carbon-components/scss/globals/scss/styles.scss'

// Global styles
import './styles/main.scss'

// Unregister any stale service workers left by previous apps on this domain
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations().then((registrations) => {
    for (const registration of registrations) {
      registration.unregister()
    }
  })
}

// Suppress SessionExpiredError from async contexts (store actions, etc.)
window.addEventListener('unhandledrejection', (event) => {
  if (event.reason && typeof event.reason === 'object' && 'isSessionExpired' in event.reason) {
    event.preventDefault()
  }
})

// Load runtime config first, then bootstrap the app
loadConfig().then(async () => {
  initClient()

  const app = createApp(App)
  const pinia = createPinia()

  app.use(pinia)
  app.use(router)
  app.use(CarbonVue)
  app.use(i18n)

  // Suppress SessionExpiredError — the overlay handles UX
  app.config.errorHandler = (err) => {
    if (err && typeof err === 'object' && 'isSessionExpired' in err) return
    throw err // Re-throw non-session errors so Vue's default handler catches them
  }

  // Load translations, init auth, then mount
  await loadRemoteMessages().catch(() => {})
  const authStore = useAuthStore()
  await authStore.init().catch(() => {})
  markAuthInitialized()
  app.mount('#app')
})
